import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const PrivacyPolicy = () => {
  const lastUpdated = 'February 13, 2026';

  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <Link to="/" className="legal-back-link">← Back to Home</Link>
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-subtitle">Last updated: {lastUpdated}</p>
        </div>

        <div className="legal-content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Neoshift ETL, operated by PT Neo Teknologi Perkasa ("we," "our," or "us"), is committed to protecting
              your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information
              when you use our AI-powered business operations automation platform and related services (collectively, the "Service").
            </p>
            <p>
              By using the Service, you consent to the data practices described in this policy. If you do not
              agree with this policy, please do not use the Service.
            </p>
            <p>
              <strong>Legal Entity:</strong> PT Neo Teknologi Perkasa
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>

            <h3>2.1 Client Information</h3>
            <p>When your organization engages our services, we collect:</p>
            <ul>
              <li>Company name, industry, and business address</li>
              <li>Authorized contact persons (names, email addresses, phone numbers)</li>
              <li>Billing contact information</li>
              <li>System integration credentials (stored in encrypted form)</li>
            </ul>

            <h3>2.2 Documents and Business Data</h3>
            <p>
              You may submit documents and data to the Service for processing. This data may include:
            </p>
            <ul>
              <li>Purchase orders, invoices, delivery orders, and proof-of-delivery documents</li>
              <li>Contracts, quotes, and RFQ documents</li>
              <li>Business rules and validation criteria</li>
              <li>ERP, CRM, and WMS records used for cross-validation</li>
            </ul>
            <p>
              You retain full ownership of your documents and business data. We process it only to provide the Service.
            </p>

            <h3>2.3 Usage Information</h3>
            <p>We automatically collect information about how the Service is used:</p>
            <ul>
              <li>Document processing volumes and types</li>
              <li>System integration and synchronization logs</li>
              <li>Error reports and processing performance data</li>
              <li>Feature usage patterns</li>
            </ul>

            <h3>2.4 Payment Information</h3>
            <p>
              For enterprise engagements, billing is typically handled via invoicing. We collect billing
              contact details and payment references necessary to process your payments. We do not store
              complete credit card or bank account numbers on our servers.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information to:</p>
            <ul>
              <li>Provide, maintain, and improve the Service</li>
              <li>Process your documents and execute automated workflows</li>
              <li>Validate extracted data against your business rules and records</li>
              <li>Synchronize data across your connected systems</li>
              <li>Send administrative communications (service updates, security alerts)</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Analyze usage patterns to improve accuracy and performance</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. AI and Machine Learning</h2>
            <p>
              Our Service uses AI and machine learning to extract data from documents, validate information
              against your business rules, and automate data processing workflows. Important points:
            </p>
            <ul>
              <li>AI processing is performed solely to provide the Service to you</li>
              <li>We do not use your business documents to train general-purpose AI models</li>
              <li>Custom AI models trained on your data are specific to your organization and not shared</li>
              <li>AI processing may involve third-party AI providers under strict confidentiality agreements</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Data Sharing and Disclosure</h2>
            <p>
              We do not sell your personal information or business data. We may share information in
              the following circumstances:
            </p>

            <h3>5.1 Service Providers</h3>
            <p>We work with third-party service providers who assist in operating the Service:</p>
            <ul>
              <li>Cloud infrastructure providers (data hosting and processing)</li>
              <li>AI service providers (document extraction and processing)</li>
              <li>Email service providers (transactional emails)</li>
            </ul>
            <p>
              These providers are bound by confidentiality obligations and can only use your data
              to provide services to us.
            </p>

            <h3>5.2 Legal Requirements</h3>
            <p>
              We may disclose information if required by law, regulation, or legal process, or if we
              believe disclosure is necessary to protect our rights, your safety, or the safety of others.
            </p>

            <h3>5.3 Business Transfers</h3>
            <p>
              In the event of a merger, acquisition, or sale of assets, your information may be
              transferred as part of that transaction. We will notify you of any such change.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Data Security</h2>
            <p>We implement comprehensive security measures to protect your data:</p>
            <ul>
              <li><strong>Encryption:</strong> All data is encrypted in transit (TLS/SSL) and at rest (AES-256)</li>
              <li><strong>Access Controls:</strong> Role-based access with authentication for all system integrations</li>
              <li><strong>Infrastructure:</strong> Enterprise-grade cloud infrastructure</li>
              <li><strong>Monitoring:</strong> Continuous monitoring for security threats and anomalies</li>
              <li><strong>Auditing:</strong> Comprehensive access and processing audit logs</li>
              <li><strong>Document Handling:</strong> Secure processing pipeline with data isolation per client</li>
            </ul>
            <p>
              While we strive to protect your data, no method of transmission or storage is 100% secure.
              We cannot guarantee absolute security.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Data Retention</h2>
            <p>
              We retain your data for as long as your service agreement is active or as needed to provide the Service.
              After contract termination:
            </p>
            <ul>
              <li>You have 30 days to request export of your data</li>
              <li>After 30 days, your business data and processed documents are permanently deleted</li>
              <li>Account and contract information may be retained for up to 90 days for legal compliance</li>
              <li>Aggregated, anonymized performance data may be retained indefinitely</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Your Rights and Choices</h2>
            <p>Depending on your jurisdiction, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your data</li>
              <li><strong>Portability:</strong> Request your data in a portable format</li>
              <li><strong>Objection:</strong> Object to certain processing activities</li>
              <li><strong>Withdrawal:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p>
              To exercise these rights, contact us at the email address below. We will respond within
              30 days (or as required by applicable law).
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Cookies and Tracking</h2>
            <p>
              Our Service primarily operates as a backend automation system with limited web-based interfaces.
              Where web interfaces are used, we may use cookies and similar technologies to:
            </p>
            <ul>
              <li>Maintain your session and authentication state</li>
              <li>Remember your preferences</li>
              <li>Improve the Service</li>
            </ul>
            <p>
              Essential cookies are required for the Service to function. You can control non-essential
              cookies through your browser settings.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. International Data Transfers</h2>
            <p>
              Your data may be processed in countries other than your own. We ensure appropriate
              safeguards are in place for international transfers, including:
            </p>
            <ul>
              <li>Standard Contractual Clauses approved by relevant authorities</li>
              <li>Adequacy decisions for certain jurisdictions</li>
              <li>Binding Corporate Rules where applicable</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>11. Children's Privacy</h2>
            <p>
              The Service is intended for business use and is not directed at individuals under the age of 18.
              We do not knowingly collect personal information from children.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by:</p>
            <ul>
              <li>Posting the updated policy on our website</li>
              <li>Sending a notification to your designated contact</li>
              <li>Including notice in service communications</li>
            </ul>
            <p>
              Your continued use of the Service after changes take effect constitutes acceptance
              of the updated policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Contact Us</h2>
            <p>If you have questions about this Privacy Policy or our data practices, please contact us:</p>
            <p className="legal-contact">
              <strong>Email:</strong> <a href="mailto:support@bi.neoshift.ai">support@bi.neoshift.ai</a>
            </p>
          </section>

          <section className="legal-section">
            <h2>14. Jurisdiction-Specific Rights</h2>

            <h3>For California Residents (CCPA)</h3>
            <p>
              California residents have additional rights under the CCPA, including the right to know
              what personal information is collected, the right to delete, and the right to opt-out of
              the sale of personal information. We do not sell personal information.
            </p>

            <h3>For European Residents (GDPR)</h3>
            <p>
              If you are in the European Economic Area, you have rights under the GDPR including access,
              rectification, erasure, restriction, portability, and the right to lodge a complaint with
              a supervisory authority.
            </p>
          </section>

        </div>

        <div className="legal-footer">
          <p>&copy; {new Date().getFullYear()} Neoshift. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
